import Label from '@/components/elements/Label/Label';
import RadioButton from '@/components/elements/forms/RadioButton';
import { ListItem, ListItemContent } from '@/components/elements/lists';
import Accordion from '@/components/elements/lists/Accordion/Accordion';
import Alert from '@/components/elements/notifications/Alert/Alert';
import Icon from '@/components/icons/Icon';
import { PaymentMethod } from '@/constants/paymentMethodConstants';
import { classnames } from '@/helpers';
import { getSelectedPaymentDetails } from '@/helpers/checkout';
import { _s } from '@/locale';
import { PaymentMethodListItem } from '@/types/checkout';
import { Fragment, useState } from 'react';

type PaymentMethodListType = 'supported' | 'unsupported' | 'bokaPayment';

type PaymentMethodsListProps = {
  paymentMethods: PaymentMethodListItem[];
  type: PaymentMethodListType;
  onClick: (paymentMethod: PaymentMethodListItem) => void;
  isGiftcardCheckout?: boolean;
  isOnlinePaymentRequiredCustomer?: boolean;
  isBokadirektPay?: boolean;
};

const baseTranslationKey = 'components.modules.checkout.PaymentMethodsList';

const PaymentMethodsList = ({
  paymentMethods = [],
  type,
  onClick,
  isGiftcardCheckout = false,
  isOnlinePaymentRequiredCustomer = false,
  isBokadirektPay = false,
}: PaymentMethodsListProps) => {
  const isSelectedHidden = paymentMethods.some((item) => item.hidden && item.isSelected);
  const [showHidden, setShowHidden] = useState(isSelectedHidden);
  const [showUnsupported, setShowUnsupported] = useState(false);

  const [hidden, visible] = paymentMethods.reduce(
    (acc, item) => {
      acc[item.hidden && type === 'supported' ? 0 : 1].push(item);
      return acc;
    },
    [[], []] as [PaymentMethodListItem[], PaymentMethodListItem[]],
  );

  return (
    <div data-cy="paymentMethodList">
      {type === 'unsupported' && (
        <button
          type="button"
          className="w-full text-start outline-none"
          onClick={() => {
            setShowUnsupported((prev) => !prev);
          }}>
          <Accordion label={_s(`${baseTranslationKey}.unsupported.title`)} size="sm" expanded={showUnsupported} />
        </button>
      )}
      {type === 'unsupported' && showUnsupported && (
        <div className="p-md">
          <Alert
            leftSlot={<Icon variant="info-circle" color="information-900" />}
            variant="information"
            body={_s(`${baseTranslationKey}.unsupported.alert`)}
          />
        </div>
      )}
      {type === 'unsupported' &&
        showUnsupported &&
        paymentMethods.map((method, key) => {
          return (
            <Fragment key={key}>
              <PaymentMethodsListItem onClick={onClick} paymentMethod={method} type={type} />
            </Fragment>
          );
        })}
      {type !== 'unsupported' &&
        visible.map((method, key) => {
          return (
            <Fragment key={key}>
              <PaymentMethodsListItem
                onClick={onClick}
                paymentMethod={method}
                type={type}
                isGiftcardCheckout={isGiftcardCheckout}
                isBokadirektPay={isBokadirektPay}
              />
            </Fragment>
          );
        })}
      {showHidden &&
        hidden.map((method, key) => {
          return (
            <Fragment key={key}>
              <PaymentMethodsListItem
                onClick={onClick}
                paymentMethod={method}
                type={type}
                isGiftcardCheckout={isGiftcardCheckout}
                isBokadirektPay={isBokadirektPay}
              />
            </Fragment>
          );
        })}
      {showHidden && !isGiftcardCheckout && (
        <div className="pb-sm">
          <Alert
            leftSlot={<Icon variant="info-circle" color="fg_info_bold" />}
            variant="information"
            verticalAlign="top"
            body={_s(
              `${baseTranslationKey}.showMoreSupported.alert${
                isOnlinePaymentRequiredCustomer ? 'OnlinePaymentRequiredCustomer' : ''
              }`,
            )}
          />
        </div>
      )}
      {hidden.length > 0 && (
        <button
          type="button"
          className="w-full pb-4 text-start outline-none"
          onClick={() => {
            setShowHidden((prev) => isSelectedHidden || !prev);
          }}>
          <Accordion
            label={
              showHidden
                ? _s(`${baseTranslationKey}.showFewerSupported.title`)
                : _s(`${baseTranslationKey}.showMoreSupported.title`)
            }
            size="sm"
            className="text-fg_link"
            labelClassName="text-l font-thin"
            rightIcon={
              <Icon color="fg_link" variant={isSelectedHidden || showHidden ? 'chevron-up' : 'chevron-down'} />
            }
            expanded={isSelectedHidden || showHidden}
          />
        </button>
      )}
    </div>
  );
};

type PaymentMethodsListItemProps = {
  paymentMethod: PaymentMethodListItem;
  type: PaymentMethodListType;
  onClick: (paymentMethod: PaymentMethodListItem) => void;
  isGiftcardCheckout?: boolean;
  isBokadirektPay?: boolean;
};

const PaymentMethodsListItem = ({
  paymentMethod,
  onClick,
  type,
  isGiftcardCheckout = false,
  isBokadirektPay = false,
}: PaymentMethodsListItemProps) => {
  const { title = null, subTitle = null, highlighted = false } = paymentMethod || {};
  const { icon, label } = getSelectedPaymentDetails(paymentMethod);
  const isStoredCard = paymentMethod.type === PaymentMethod.STORED_ONLINE_CARD;
  const isKlarnaOrWalley = paymentMethod.type === PaymentMethod.KLARNA || paymentMethod.type === PaymentMethod.WALLEY;
  const hideSubTitle = (type === 'bokaPayment' || isBokadirektPay) && !isKlarnaOrWalley;

  return (
    <RadioButton
      variant="large"
      iconPosition="right"
      className="my-md border-border_regular rounded-md border py-0"
      disabled={type === 'unsupported'}
      onClick={() => onClick(paymentMethod)}
      iconType="icon"
      checked={paymentMethod?.isSelected}>
      <div className="flex w-full items-start text-start">
        <Icon
          variant={icon}
          style={{ width: 32, height: 32 }}
          className={classnames('m-md', type === 'unsupported' && 'opacity-40')}
          noFilter
        />
        <ListItem
          verticalAlign="top"
          leftPadding={false}
          rightPadding={false}
          className={classnames('py-sm min-h-[66px] items-center', type === 'unsupported' && 'opacity-40')}>
          {highlighted && (
            <div>
              <Label
                label={_s(`${baseTranslationKey}.highlighted`)}
                className="rounded-sm bg-[#1A6437] px-2 align-middle leading-6 text-white"
              />
            </div>
          )}
          <ListItemContent
            title={isStoredCard ? label : title ?? label}
            subTitleClassName="text-m"
            {...(!hideSubTitle && { subTitle: !isGiftcardCheckout ? subTitle ?? label : undefined })}
          />
        </ListItem>
      </div>
    </RadioButton>
  );
};

export default PaymentMethodsList;
